import { Link } from "react-router-dom";
import type { NewsletterPost } from "@/lib/cms-newsletter";
import {
  formatArchiveDate,
  postExcerpt,
  postTimestamp,
  variedReadMinutes,
} from "@/components/newsletter/newsletter-post-utils";

/** Bottom scrim: black only (no blur)—transparent at top, stronger toward bottom for text. */
const scrimHero =
  "rounded-b-[0.875rem] bg-[linear-gradient(to_top,rgba(0,0,0,0.88)_0%,rgba(0,0,0,0.48)_42%,rgba(0,0,0,0.12)_68%,rgba(0,0,0,0)_100%)] px-5 pb-5 pt-12 md:rounded-b-2xl md:px-8 md:pb-7 md:pt-16 lg:px-10 lg:pb-9 lg:pt-[4.25rem]";

const scrimHalf =
  "rounded-b-[0.875rem] bg-[linear-gradient(to_top,rgba(0,0,0,0.85)_0%,rgba(0,0,0,0.44)_40%,rgba(0,0,0,0.1)_65%,rgba(0,0,0,0)_100%)] px-5 pb-5 pt-10 md:rounded-b-2xl md:px-6 md:pb-6 md:pt-12";

function IconCalendar({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

function IconClock({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function MetaRow({ post, className = "" }: { post: NewsletterPost; className?: string }) {
  const ts = postTimestamp(post);
  const dateLabel = formatArchiveDate(ts);
  const mins = variedReadMinutes(post);
  return (
    <div
      className={`mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/75 ${className}`}
    >
      {dateLabel ? (
        <span className="inline-flex items-center gap-1.5 tabular-nums">
          <IconCalendar className="h-4 w-4 shrink-0 text-white/55" />
          <time dateTime={ts != null ? new Date(ts * 1000).toISOString() : undefined}>
            {dateLabel}
          </time>
        </span>
      ) : null}
      <span className="inline-flex items-center gap-1.5">
        <IconClock className="h-4 w-4 shrink-0 text-white/55" />
        {mins} min read
      </span>
    </div>
  );
}

function FeaturedHeroCard({ post }: { post: NewsletterPost }) {
  const excerpt = postExcerpt(post);
  return (
    <Link
      to={`/newsletter/${post.slug}`}
      className="group relative isolate flex h-[310px] w-full overflow-hidden rounded-2xl border border-[#FFFFFF52] md:h-[450px]"
    >
      {post.thumbnailUrl ? (
        <img
          src={post.thumbnailUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="absolute inset-0 bg-[#111110]" />
      )}
      <div className="relative z-10 mt-auto w-full">
        <div className={scrimHero}>
          <h2 className="max-w-4xl font-fira-condensed text-xl font-semibold leading-tight tracking-tight text-white [text-shadow:0_1px_2px_rgba(0,0,0,.6)] md:text-2xl lg:text-[28px]">
            {post.title}
          </h2>
          {excerpt ? (
            <p className="mt-3 max-w-3xl text-[13px] leading-relaxed text-white/85 line-clamp-3 md:text-[15px] [text-shadow:0_1px_2px_rgba(0,0,0,.55)]">
              {excerpt}
            </p>
          ) : null}
          <MetaRow post={post} className="text-white/85 [text-shadow:0_1px_2px_rgba(0,0,0,.5)]" />
        </div>
      </div>
    </Link>
  );
}

function FeaturedHalfCard({ post }: { post: NewsletterPost }) {
  return (
    <Link
      to={`/newsletter/${post.slug}`}
      className="group relative isolate flex h-[310px] w-full overflow-hidden rounded-2xl border border-[#FFFFFF52] md:h-[320px]"
    >
      {post.thumbnailUrl ? (
        <img
          src={post.thumbnailUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="absolute inset-0 bg-[#111110]" />
      )}
      <div className="relative z-10 mt-auto w-full">
        <div className={scrimHalf}>
          <h3 className="font-fira-condensed text-base font-semibold leading-snug text-white line-clamp-3 [text-shadow:0_1px_2px_rgba(0,0,0,.6)] md:text-lg">
            {post.title}
          </h3>
          <MetaRow post={post} className="text-white/85 [text-shadow:0_1px_2px_rgba(0,0,0,.5)]" />
        </div>
      </div>
    </Link>
  );
}

type Props = {
  posts: NewsletterPost[];
  loading?: boolean;
};

export default function NewsletterFeaturedTop3({ posts, loading }: Props) {
  const [first, second, third] = posts;

  if (loading) {
    return (
      <section
        className="w-full bg-black pb-8 font-fira text-white pt-4 sm:pb-10"
        aria-hidden
      >
        <div className="mx-auto w-[90%] space-y-4 md:w-[80%]">
          <div className="h-[310px] animate-pulse rounded-2xl bg-white/[0.06] md:h-[450px]" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="h-[310px] animate-pulse rounded-2xl bg-white/[0.06] md:h-[320px]" />
            <div className="h-[310px] animate-pulse rounded-2xl bg-white/[0.06] md:h-[320px]" />
          </div>
        </div>
      </section>
    );
  }

  if (!first) return null;

  return (
    <section
      className="w-full bg-black pb-8 font-fira text-white pt-4 sm:pb-10"
      aria-label="Featured newsletter issues"
    >
      <div className="mx-auto w-[90%] max-w-[100vw] space-y-4 md:w-[80%] md:space-y-5">
        <FeaturedHeroCard post={first} />
        {(second || third) && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
            {second ? <FeaturedHalfCard post={second} /> : null}
            {third ? <FeaturedHalfCard post={third} /> : null}
          </div>
        )}
      </div>
    </section>
  );
}